import { supabase, isSupabaseConfigured } from '@/lib/supabase/client'

const DEV_ORG_SLUG = 'masjid-al-amanah'
const FORCE_DEV_MODE = import.meta.env.VITE_DEV_MODE === 'true'

// Use type assertion for tables not in generated types
const db = supabase as unknown as {
  from: (table: string) => {
    select: (query: string) => {
      eq: (column: string, value: string) => {
        limit: (count: number) => {
          maybeSingle: () => Promise<{ data: { organization: { slug: string; status: string } } | null }>
        }
      }
    }
  }
}

/**
 * Determines the correct redirect URL after login based on user role.
 *
 * Redirect logic:
 * - platform_admin -> /platform
 * - owner/delegate -> /{slug}/admin
 * - member -> /{slug}/portal
 * - no organization -> /no-organization
 */
export async function getPostLoginRedirectUrl(userEmail: string): Promise<string> {
  // Production mode - check user role in database
  const { data: { user } } = await supabase.auth.getUser()

  // Dev Routing logic (development builds only, via mock user IDs)
  const isMockUser = user?.id?.startsWith('dev-')

  // import.meta.env.DEV is false in production builds, so this entire branch
  // (and the hardcoded DEV_ORG_SLUG) is dead code there. Routing no longer keys
  // off the user's email domain; in prod it resolves from the real
  // organization_memberships below.
  if (import.meta.env.DEV && (FORCE_DEV_MODE || !isSupabaseConfigured() || isMockUser)) {
    console.log('[REDIRECT] Using Dev Routing for:', userEmail)

    if (userEmail.startsWith('admin@')) {
      return '/platform'
    }

    if (userEmail.startsWith('imam@')) {
      return `/${DEV_ORG_SLUG}/admin`
    }

    if (userEmail.startsWith('member@')) {
      return `/${DEV_ORG_SLUG}/portal`
    }

    // Fallback for other dev emails
    return '/platform'
  }

  if (!user) return '/login'

  // 1. Check if platform admin
  const { data: platformAdmin } = await supabase
    .from('platform_admins')
    .select('id')
    .eq('user_id', user.id)
    .maybeSingle()

  if (platformAdmin) {
    return '/platform'
  }

  // 2. Check if organization owner
  const { data: ownerOrg } = await db
    .from('organization_owners')
    .select('organization:organizations(slug, status)')
    .eq('user_id', user.id)
    .limit(1)
    .maybeSingle()

  if (ownerOrg?.organization && typeof ownerOrg.organization === 'object' && 'slug' in ownerOrg.organization) {
    const org = ownerOrg.organization as { slug: string; status: string }

    switch (org.status) {
      case 'pending':
        return '/pending-approval'
      case 'rejected':
        return '/no-organization'
      case 'approved':
        return `/${org.slug}/admin`
      default:
        return '/no-organization'
    }
  }

  // 3. Check if organization delegate
  const { data: delegateOrg } = await db
    .from('organization_delegates')
    .select('organization:organizations(slug, status)')
    .eq('user_id', user.id)
    .limit(1)
    .maybeSingle()

  if (delegateOrg?.organization && typeof delegateOrg.organization === 'object' && 'slug' in delegateOrg.organization) {
    const org = delegateOrg.organization as { slug: string; status: string }

    switch (org.status) {
      case 'pending':
        return '/pending-approval'
      case 'rejected':
        return '/no-organization'
      case 'approved':
        return `/${org.slug}/admin`
      default:
        return '/no-organization'
    }
  }

  // 4. Check if organization member
  const { data: memberOrg } = await db
    .from('organization_members')
    .select('organization:organizations(slug)')
    .eq('user_id', user.id)
    .limit(1)
    .maybeSingle()

  if (memberOrg?.organization && typeof memberOrg.organization === 'object' && 'slug' in memberOrg.organization) {
    return `/${memberOrg.organization.slug}/portal`
  }

  // Fallback - user has no specific organization context (Global User)
  return '/dashboard'
}
